import Table from "components/DataTable/Table";
import RefreshButton from "components/RefreshButton/RefreshButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function CusQuotes({ selectedCustomer }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   ALL STATES
   ********************************************* */
  const [customerQuotes, setCustomerQuotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("all");

  /*   All Functions
   ********************************************* */

  const getCustomerQuotes = async () => {
    try {
      let _filterBy = searchParams.get("quote-type");
      if (!_filterBy) {
        _filterBy = "all";
      }
      setLoading(true);
      const { data } = await API.post(
        "/customers/quotes/" + selectedCustomer?.id,
        {
          filterBy: _filterBy,
        },
        {
          headers: authHeader(),
        },
      );
      setCustomerQuotes(data);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  // Getting 12 Hours Time from SQL timestamp
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    const time = date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return time;
  };

  const handleDetailsClick = (item) => {
    let status =
      item?.WOStatus === "pending" || item?.WOStatus === "processing"
        ? "pending-processing"
        : item.WOStatus;

    window.location.href = `/quotes/details/${status}/${item?.quoteId}?page=1&tab=quote-details`;
  };

  /*   Table functions
   ********************************************* */
  const table_head = [
    "Q-ID",
    "Job Name",
    "Brand",
    "Created Date",
    "Type",
    "Status",
    "Actions",
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="py-1 align-middle me-0">Q-{item?.quoteId}</td>
      <td className="py-1 align-middle me-0">{item?.jobName?.slice(0, 30)}</td>
      <td className="py-1 align-middle me-0">{item?.location?.name}</td>
      <td className="py-1 align-middle me-0">
        {convertSqlTimestampToDate(item.createdAt)}{" "}
      </td>

      <td className="py-1 align-middle me-0">
        {item?.bidId ? "Bid" : "Quote"}
      </td>
      <td className="py-1 align-middle me-0">{item?.status}</td>
      <td className="table-action d-flex py-1 align-middle me-0">
        <button
          onClick={(e) => {
            handleDetailsClick(item);
          }}
          className={`btn btn-sm bg-success text-white mx-1 `}
        >
          Details
        </button>
      </td>
    </tr>
  );

  useEffect(() => {
    let filterBy = searchParams.get("quote-type");
    if (filterBy) {
      setSelectedTab(filterBy);
    } else {
      setSelectedTab(null);
      updateSearchParams("quote-type", "all");
    }
  }, [searchParams]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      let status = searchParams.get("quote-type");
      if (status && selectedCustomer?.id) {
        getCustomerQuotes();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [searchParams, selectedCustomer]);

  // remove "quote-type" from search params when component unmounts

  return (
    <div className="py-4 px-3 w-100" style={{ borderRadius: 10 }}>
      {loading && <Spinner />}
      {/* This tab has no toolbar of its own, so the refresh sits above the
          table on its own row rather than being wedged into the tab strip. */}
      <div className="d-flex justify-content-end mb-2">
        <RefreshButton loading={loading} onClick={() => getCustomerQuotes()} />
      </div>
      {/* Tabs */}
      <div className="d-none">
        <button
          className={`btn ${
            selectedTab === "all" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            updateSearchParams("quote-type", "all");
          }}
        >
          All
        </button>

        <button
          className={`btn ${
            selectedTab === "quote" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            updateSearchParams("quote-type", "quote");
          }}
        >
          Quotes
        </button>
        <button
          className={`btn ${
            selectedTab === "bid" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            updateSearchParams("quote-type", "bid");
          }}
        >
          Bids
        </button>
      </div>
      {/*   Table
       ********************************************* */}
      <div className="px-2" style={{ minHeight: "50vh", borderRadius: 15 }}>
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <Table
            table_head={table_head}
            table_body={table_body}
            table_data={customerQuotes}
          />
        </div>
      </div>
    </div>
  );
}
